import { useState, useCallback } from 'react';

import { NotificationSeverity } from '@/components/notifications/Notification';
import useNotification from '@/hooks/useNotification';
import {
  ExportJobStatus,
  RoutesFilter,
} from '@/pages/Business/pages/Monitoring/models';
import {
  useInitiateExportMutation,
  useLazyGetExportJobStatusQuery,
} from '@/pages/Business/pages/Monitoring/services/routes.service';
import { getErrorMessage } from '@/utils/errorUtils';

const POLLING_INTERVAL_MS = 3000;
const MAX_POLLING_DURATION_MS = 15 * 60 * 1000;

export const useRouteVisitsExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [initiateExport] = useInitiateExportMutation();
  const [getJobStatus] = useLazyGetExportJobStatusQuery();
  const { showNotification } = useNotification();

  const pollJobStatus = useCallback(
    async (jobId: string, startedAtMs: number) => {
      try {
        if (Date.now() - startedAtMs > MAX_POLLING_DURATION_MS) {
          throw new Error(
            'El reporte tardó demasiado en generarse. Verifique que Hermes esté procesando el evento shipping_export_excel.'
          );
        }

        const { data: job, error } = await getJobStatus(jobId);

        if (error || !job) {
          throw new Error('Error al consultar el estado del reporte');
        }

        if (job.status === ExportJobStatus.COMPLETED && job.downloadUrl) {
          const link = document.createElement('a');
          link.href = job.downloadUrl;
          link.setAttribute('download', job.fileName || 'reporte-visitas.xlsx');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          showNotification(
            'Reporte generado con éxito',
            NotificationSeverity.success
          );
          setIsExporting(false);
          return;
        }

        if (job.status === ExportJobStatus.ERRORED) {
          throw new Error(
            job.errorMessage || 'Error en el servidor al generar el reporte'
          );
        }

        setTimeout(
          () => pollJobStatus(jobId, startedAtMs),
          POLLING_INTERVAL_MS
        );
      } catch (err: unknown) {
        const message = getErrorMessage(err, 'Error al generar el reporte');
        showNotification(message, NotificationSeverity.error);
        setIsExporting(false);
      }
    },
    [getJobStatus, showNotification]
  );

  const handleExport = useCallback(
    async (filters: RoutesFilter) => {
      if (isExporting) return;

      setIsExporting(true);
      try {
        if (
          !filters.routeId?.trim() &&
          (!filters.scheduledFrom || !filters.scheduledTo)
        ) {
          throw new Error(
            'Debe indicar scheduledFrom y scheduledTo (fecha programada de la ruta). El rango no puede superar 35 días.'
          );
        }
        const response = await initiateExport(filters).unwrap();

        showNotification(
          'Generando reporte, por favor espere...',
          NotificationSeverity.info
        );

        pollJobStatus(response.jobId, Date.now());
      } catch (err: unknown) {
        const message = getErrorMessage(err, 'No se pudo iniciar la exportación');
        showNotification(message, NotificationSeverity.error);
        setIsExporting(false);
      }
    },
    [initiateExport, isExporting, pollJobStatus, showNotification]
  );

  return {
    handleExport,
    isExporting,
  };
};
